import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  VStack,
  HStack,
  Badge,
  Icon,
  Flex,
  IconButton,
  Tooltip,
  Center,
  Button,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import {
  CheckCircleIcon,
  TimeIcon,
  WarningIcon,
  CloseIcon,
  InfoIcon,
} from '@chakra-ui/icons';
import {
  FaFileExcel,
  FaFilter,
  FaEye,
  FaCalendarAlt,
  FaRegFileAlt,
} from 'react-icons/fa';

import { format } from 'date-fns';

const UploadedFilesTable = ({
  SchemeModalOpen,
  SchemeModalClose,
  files = [],
  isOpenDetails,
  onCloseDetails,
  currentPage = 1,
  itemsPerPage = 10,
  onViewClick,
  onEditClick,
  onDownloadClick,
  userRole = '',
  searchParams = new URLSearchParams(),
  onClearFilters = () => { },
  setSearchParams,
  setPage,
  isLoading = false,
  ...props
}) => {
  const textColor = useColorModeValue('gray.1000', 'gray.100');
  const subtitleColor = useColorModeValue('gray.600', 'gray.300');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const tableHeaderBg = useColorModeValue('teal.50', 'teal.800');
  const rowHoverBg = useColorModeValue('gray.50', 'gray.700');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFinancialYear, setSelectedFinancialYear] = useState('');
  const [financialYears, setFinancialYears] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const User = localStorage.getItem('user');
  let UserData = JSON.parse(User);
  const role = UserData?.role;
  const getCurrentFinancialYear = () => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    if (currentMonth >= 3) {
      return `${currentYear}-${currentYear + 1}`;
    } else {
      return `${currentYear - 1}-${currentYear}`;
    }
  };

  const generateFinancialYears = () => {
    const years = [];
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    const endYear = currentMonth >= 3 ? currentYear : currentYear - 1;

    for (let i = 2023; i <= endYear; i++) {
      years.push(`${i}-${i + 1}`);
    }
    return years.reverse();
  };

  const formatDateValueToFY = (dateValue) => {
    if (!dateValue) return getCurrentFinancialYear();
    try {
      const year = new Date(dateValue).getFullYear();
      return `${year}-${year + 1}`;
    } catch (e) {
      return dateValue;
    }
  };

  const activeFilterFY =
    searchParams.get('financialYear') || getCurrentFinancialYear();

  useEffect(() => {
    const generatedYears = generateFinancialYears();
    setFinancialYears(generatedYears);

    if (searchParams.has('financialYear')) {
      const yearFromParams = searchParams.get('financialYear');
      setSelectedFinancialYear(yearFromParams);
    } else {
      const currentFY = getCurrentFinancialYear();
      setSelectedFinancialYear(currentFY);
    }
  }, [searchParams]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'green';
      case 'rejected':
        return 'red';
      case 'pending':
        return 'orange';
      case 'submitted':
        return 'blue';
      default:
        return 'gray';
    }
  };

  const getFileFinancialYear = (file) => {
    return (
      file?.financialYear ||
      file?.fy ||
      file?.year ||
      (file?.createdAt ? extractYearFromDate(file.createdAt) : '-')
    );
  };

  const extractYearFromDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = date.getMonth();

      if (month >= 3) {
        return `${year}-${year + 1}`;
      } else {
        return `${year - 1}-${year}`;
      }
    } catch {
      return '-';
    }
  };

  const handleEditClick = (file) => {
    if (file.status === 'approved' || file.status === 'rejected') {
      setSelectedFile(file);
      onOpen();
    } else {
      onEditClick && onEditClick(file);
    }
  };

  const confirmEdit = () => {
    if (selectedFile) {
      onEditClick && onEditClick(selectedFile);
      onClose();
    }
  };



  const formatSmartTime = (inputDate) => {
    if (!inputDate) return "";

    const date = new Date(inputDate);
    const now = new Date();

    const diffInMs = now - date;
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMinutes / 60);

    const isSameDay =
      date.getDate() === now.getDate() &&
      date.getMonth() === now.getMonth() &&
      date.getFullYear() === now.getFullYear();

    // ✅ Just now
    if (diffInMinutes < 1) return "Just now";

    // ✅ Less than 1 hour
    if (diffInMinutes < 60) return `${diffInMinutes} min ago`;

    // ✅ Same day → show time
    if (isSameDay) {
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    }

    // ✅ After 1 day → show date + time
    return date.toLocaleString([], {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (isLoading) {
    return (
      <Center py={8}>
        <VStack spacing={3}>
          <Text color={subtitleColor}>Loading files...</Text>
        </VStack>
      </Center>
    );
  }

  if (files.length === 0) {
    return (
      <Center py={8}>
        <VStack spacing={3}>
          <Text fontSize="sm" color={textColor}>
            No files found for {selectedFinancialYear}
          </Text>
          {searchParams.toString() && (
            <Button
              size="xs"
              colorScheme="blue"
              variant="outline"
              onClick={onClearFilters}
              leftIcon={<Icon as={FaFilter} />}
            >
              Clear Filters
            </Button>
          )}
        </VStack>
      </Center>
    );
  }

  return (
    <>
      <Box
        overflowX="auto"
        maxHeight="400px"
        overflowY="auto"
        position="relative"
        {...props}
      >
        <Table variant="simple" size="md" position="relative">
          {/* Fixed Header */}
          <Thead bg={tableHeaderBg} position="sticky" top={0} zIndex={10}>
            <Tr>
              <Th
                color={textColor}
                fontSize="xs"
                fontWeight="semibold"
                py={3}
                borderBottom="1px solid"
                borderColor={borderColor}
                position="sticky"
                top={0}
                bg={tableHeaderBg}
                minWidth="30px"
              >
                SR.NO
              </Th>
              <Th
                color={textColor}
                fontSize="xs"
                fontWeight="semibold"
                py={3}
                borderBottom="1px solid"
                borderColor={borderColor}
                position="sticky"
                top={0}
                bg={tableHeaderBg}
                minWidth="80px"
              >
                <Flex gap={'10px'} alignItems="center">
                  Financial Year
                </Flex>
              </Th>
              <Th
                color={textColor}
                fontSize="xs"
                fontWeight="semibold"
                py={3}
                borderBottom="1px solid"
                borderColor={borderColor}
                position="sticky"
                top={0}
                bg={tableHeaderBg}
                minWidth="200px"
              >
                Scheme
              </Th>
              <Th
                color={textColor}
                fontSize="xs"
                fontWeight="semibold"
                py={3}
                borderBottom="1px solid"
                borderColor={borderColor}
                position="sticky"
                top={0}
                bg={tableHeaderBg}
                minWidth="220px"
              >
                <Flex gap={'10px'} alignItems="center">
                  File Type
                  {/* <Icon
                    as={FaFilter}
                    boxSize={3}
                    cursor="pointer"
                    _hover={{ color: 'blue.500' }}
                    onClick={SchemeModalOpen}
                  /> */}
                </Flex>
              </Th>



              <Th
                color={textColor}
                fontSize="xs"
                fontWeight="semibold"
                py={3}
                borderBottom="1px solid"
                borderColor={borderColor}
                position="sticky"
                top={0}
                bg={tableHeaderBg}
                minWidth="70px"
              >
                Status
              </Th>

              <Th
                color={textColor}
                fontSize="xs"
                fontWeight="semibold"
                py={3}
                borderBottom="1px solid"
                borderColor={borderColor}
                position="sticky"
                top={0}
                textAlign="center"
                bg={tableHeaderBg}
                minWidth="145px"
              >
                Date & Time
              </Th>


              {role === 'admin' ? (
                ''
              ) : (
                <Th
                  color={textColor}
                  fontSize="xs"
                  fontWeight="semibold"
                  py={3}
                  borderBottom="1px solid"
                  borderColor={borderColor}
                  textAlign="center"
                  position="sticky"
                  top={0}
                  bg={tableHeaderBg}
                  minWidth="55px"
                >
                  Action
                </Th>
              )}
            </Tr>
          </Thead>

          <Tbody>
            {(Array.isArray(files) ? files : []).map((file, idx) => {
              const fileFY = getFileFinancialYear(file);
              const isMatch = fileFY === activeFilterFY;

              return (
                <Tr
                  key={file?._id || idx}
                  _hover={{ bg: rowHoverBg }}
                  transition="background 0.2s"
                >
                  <Td py={4} >
                    <Text fontSize="xs">
                      {(currentPage - 1) * itemsPerPage + idx + 1}
                    </Text>
                  </Td>

                  <Td py={4} minWidth="150px">
                    <HStack spacing={2}>
                      <Icon as={FaCalendarAlt} color="orange.400" boxSize={3} />
                      <Badge
                        colorScheme="orange"
                        variant={isMatch ? 'solid' : 'subtle'}
                        px={2}
                        borderRadius="md"
                        fontSize="xs"
                      >
                        {formatDateValueToFY(
                          searchParams.get('financialYear')
                          // getCurrentFinancialYear(),
                        )}{console.log('file financial year:', fileFY, 'active filter FY:', activeFilterFY)}
                      </Badge>
                    </HStack>
                  </Td>

                  <Td py={4} >
                    <HStack spacing={2}>
                      <Icon as={FaRegFileAlt} color="blue.300" />
                      <Text fontWeight="medium" fontSize="sm">
                        {file?.scheme?.schemeName || file?.scheme?.name || '-'}
                      </Text>
                    </HStack>
                  </Td>

                  <Td py={4}>
                    <HStack spacing={2}>
                      <Icon as={FaFileExcel} color="green.500" />
                      <Tooltip label={file?.name} hasArrow>
                        <Text
                          fontSize="sm"
                          fontWeight="medium"
                          noOfLines={2}
                          maxW="550px"
                        >
                          {file?.name || 'Unnamed File'}
                        </Text>
                      </Tooltip>
                    </HStack>
                  </Td>

                  <Td py={4} >
                    <HStack spacing={2}>

                      <Text
                        color={getStatusColor(file?.status)}
                        fontSize="0.7em"
                        px={2}
                        py={1}
                        borderRadius="md"
                        textTransform="capitalize"
                      >
                        {file?.status}
                      </Text>

                    </HStack>
                  </Td>


                  <Td py={4} justify="center">
                    <Flex justify="center" >
                      <HStack spacing={2} wrap="wrap">
                        <Text fontSize="xs" color="gray.500">
                          {formatSmartTime(file?.lastUpdatedDate) || "-"}
                        </Text>
                      </HStack>
                    </Flex>
                  </Td>


                  {role === 'admin' ? null : (
                    <Td
                      py={4}
                      onClick={(e) => e.stopPropagation()}
                      minWidth="50px"
                    >
                      <Flex justify="center" gap={2}>
                        <Tooltip label="View/Edit Records" hasArrow>
                          <IconButton
                            aria-label="View"
                            icon={<FaEye />}
                            size="lg"
                            colorScheme="blue"
                            variant="ghost"
                            onClick={() => handleEditClick(file._id)}
                          />
                        </Tooltip>
                      </Flex>
                    </Td>
                  )}
                </Tr>
              );
            })}
          </Tbody>
        </Table>

        {/* Empty state */}
        {files.length === 0 && (
          <Box
            height="300px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            bg="gray.50"
          >
            <VStack spacing={3}>
              <Icon as={FaFileExcel} boxSize={8} color="gray.400" />
              <Text color="gray.500">No files found</Text>
            </VStack>
          </Box>
        )}
      </Box>

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit File Confirmation</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                This file is already {selectedFile?.status}. Editing will change
                its status back to "Submitted" and require re-approval.
              </Alert>

              <Box>
                <Text fontWeight="bold" mb={2}>
                  File Details:
                </Text>
                <VStack align="stretch" spacing={1} pl={2}>
                  <Text>
                    <strong>File:</strong>{' '}
                    {selectedFile?.fileName || selectedFile?.name || 'N/A'}
                  </Text>
                  <Text>
                    <strong>Financial Year:</strong>{' '}
                    {getFileFinancialYear(selectedFile) || 'N/A'}
                  </Text>
                  <Text>
                    <strong>Scheme:</strong>{' '}
                    {selectedFile?.scheme?.name || 'N/A'}
                  </Text>
                  <Text>
                    <strong>Taluka:</strong>{' '}
                    {selectedFile?.tahsil?.name || 'N/A'}
                  </Text>
                  <Text>
                    <strong>Status:</strong>{' '}
                    <Badge colorScheme={getStatusColor(selectedFile?.status)}>
                      {selectedFile?.status}
                    </Badge>
                  </Text>
                </VStack>
              </Box>

              <Text color="gray.600" fontSize="sm">
                Are you sure you want to edit this file? It will need to be
                reviewed and approved again after editing.
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="gray" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="orange" onClick={confirmEdit}>
              Yes, Edit File
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default UploadedFilesTable;
